import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import {
  FaChevronDown,
  FaChevronUp,
  FaUserTie,
  FaVideo,
  FaBullhorn,
  FaUsers,
  FaBookOpen,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";

const TeacherDashboard = () => {
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [lectureAccessMap, setLectureAccessMap] = useState({});
  const [previewedLectures, setPreviewedLectures] = useState([]);
  const [announcement, setAnnouncement] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [pdfLinks, setPdfLinks] = useState(["", "", "", "", ""]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [enrollMessage, setEnrollMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [editState, setEditState] = useState({});
  const [studentSearchTerm, setStudentSearchTerm] = useState("");
  const [showStudents, setShowStudents] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState("upload");

  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  useEffect(() => {
    const fetchUserAndData = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUser = sessionData.session?.user;
      if (!currentUser) return;

      const { data: userProfile } = await supabase
        .from("users")
        .select("*")
        .eq("id", currentUser.id)
        .single();
      setUser(userProfile);

      const { data: courseJoinData } = await supabase
        .from("course_teachers")
        .select("course:course_id(*)")
        .eq("teacher_id", currentUser.id);
      const coursesData = courseJoinData?.map((item) => item.course) || [];
      setCourses(coursesData);

      const { data: studentsData } = await supabase
        .from("users")
        .select("id, name, email")
        .eq("role", "student");
      setStudents(studentsData || []);
    };

    fetchUserAndData();
  }, []);

  useEffect(() => {
    const fetchCourseDetails = async () => {
      if (!selectedCourseId) return;

      const { data: enrolled } = await supabase
        .from("course_enrollments")
        .select("student_id, users:student_id(id, name, email)")
        .eq("course_id", selectedCourseId);

      setEnrolledStudents(
        (enrolled?.map((e) => e.users) || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      );

      const { data: announcementsData } = await supabase
        .from("announcements")
        .select("*")
        .eq("course_id", selectedCourseId)
        .order("created_at", { ascending: false });
      setAnnouncements(announcementsData || []);

      const { data: lecturesData } = await supabase
        .from("lectures")
        .select("*")
        .eq("course_id", selectedCourseId)
        .order("created_at", { ascending: true });
      setLectures(lecturesData || []);

      const { data: accessData } = await supabase
        .from("lecture_access")
        .select("lecture_id, student_id, can_view");

      const accessMap = {};
      accessData?.forEach(({ lecture_id, student_id, can_view }) => {
        if (!accessMap[lecture_id]) accessMap[lecture_id] = {};
        accessMap[lecture_id][student_id] = can_view;
      });
      setLectureAccessMap(accessMap);
    };

    fetchCourseDetails();
  }, [selectedCourseId]);

  const handlePostAnnouncement = async () => {
    if (!selectedCourseId || !announcement.trim()) return;

    await supabase.from("announcements").insert({
      course_id: selectedCourseId,
      content: announcement.trim(),
    });

    setAnnouncement("");
    const { data } = await supabase
      .from("announcements")
      .select("*")
      .eq("course_id", selectedCourseId)
      .order("created_at", { ascending: false });
    setAnnouncements(data || []);
  };

  const handleDeleteAnnouncement = async (id) => {
    await supabase.from("announcements").delete().eq("id", id);
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  };

  const handleDeleteLecture = async (lectureId) => {
    await supabase.from("lectures").delete().eq("id", lectureId);
    setLectures((prev) => prev.filter((lec) => lec.id !== lectureId));
  };

  const handlePdfLinkChange = (lecId, index, value) => {
    setEditState((prev) => ({
      ...prev,
      [lecId]: {
        ...prev[lecId],
        pdf_urls: {
          ...(prev[lecId]?.pdf_urls || {}),
          [index]: value,
        },
      },
    }));
  };

  const handleLectureUpload = async () => {
    if (!selectedCourseId || !videoTitle) {
      setUploadError("Please select a course and enter a lecture title.");
      return;
    }
    setUploadError("");

    const previewUrl = videoLink.includes("/view")
      ? videoLink.replace("/view", "/preview")
      : videoLink;

    const validPdfLinks = pdfLinks.filter((link) => link.trim() !== "");

    const { data: lecture } = await supabase
      .from("lectures")
      .insert({
        course_id: selectedCourseId,
        title: videoTitle,
        video_url: previewUrl,
        pdf_urls: validPdfLinks,
      })
      .select()
      .single();

    if (lecture) {
      const accessInserts = enrolledStudents.map((s) => ({
        lecture_id: lecture.id,
        student_id: s.id,
        can_view: true,
      }));
      await supabase.from("lecture_access").insert(accessInserts);
    }

    setVideoTitle("");
    setVideoLink("");
    setPdfLinks(["", "", "", "", ""]);

    const { data } = await supabase
      .from("lectures")
      .select("*")
      .eq("course_id", selectedCourseId)
      .order("created_at", { ascending: true });
    setLectures(data || []);
  };

  const handleRemoveStudent = async (studentId) => {
    await supabase
      .from("course_enrollments")
      .delete()
      .eq("student_id", studentId)
      .eq("course_id", selectedCourseId);
    setEnrolledStudents((prev) => prev.filter((s) => s.id !== studentId));
  };

  const handleEnrollStudent = async () => {
    if (!selectedCourseId || !selectedStudentId) return;

    const { data: exists } = await supabase
      .from("course_enrollments")
      .select("*")
      .eq("course_id", selectedCourseId)
      .eq("student_id", selectedStudentId);
    if (exists?.length > 0) {
      setEnrollMessage("Already enrolled.");
      return;
    }

    await supabase.from("course_enrollments").insert({
      course_id: selectedCourseId,
      student_id: selectedStudentId,
    });

    const student = students.find((s) => s.id === selectedStudentId);
    if (student) {
      setEnrolledStudents((prev) =>
        [...prev, student].sort((a, b) => a.name.localeCompare(b.name))
      );
    }
  };

  const handleEditLecture = (id, field, value) => {
    setEditState((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSaveLecture = async (id) => {
    const changes = editState[id];
    if (!changes) return;

    const existingLecture = lectures.find((l) => l.id === id);
    const updatedLecture = {
      title: changes.title ?? existingLecture.title,
      video_url: changes.video_url ?? existingLecture.video_url,
      pdf_urls: existingLecture.pdf_urls ? [...existingLecture.pdf_urls] : [],
    };

    if (changes.pdf_urls) {
      Object.entries(changes.pdf_urls).forEach(([idx, val]) => {
        updatedLecture.pdf_urls[Number(idx)] = val;
      });
    }

    await supabase.from("lectures").update(updatedLecture).eq("id", id);

    setEditState((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });

    const { data } = await supabase
      .from("lectures")
      .select("*")
      .eq("course_id", selectedCourseId)
      .order("created_at", { ascending: true });
    setLectures(data || []);
  };

  const togglePreview = (id) => {
    setPreviewedLectures((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAccessToggle = async (lectureId, studentId, canView) => {
    const { data: existing } = await supabase
      .from("lecture_access")
      .select("id")
      .eq("lecture_id", lectureId)
      .eq("student_id", studentId)
      .maybeSingle();

    if (existing) {
      await supabase
        .from("lecture_access")
        .update({ can_view: canView })
        .eq("id", existing.id);
    } else {
      await supabase.from("lecture_access").insert({
        lecture_id: lectureId,
        student_id: studentId,
        can_view: canView,
      });
    }

    setLectureAccessMap((prev) => ({
      ...prev,
      [lectureId]: {
        ...(prev[lectureId] || {}),
        [studentId]: canView,
      },
    }));
  };

  const filteredStudents = enrolledStudents.filter((student) =>
    student.name.toLowerCase().includes(studentSearchTerm.toLowerCase())
  );

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
        color: "#fff",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Sidebar */}
     <aside
  style={{
    position: "fixed",          // ✅ overlay, not part of flex layout
    top: 0,
    left: sidebarOpen ? "0px" : "-250px", // slides in/out
    height: "100%",
    width: "250px",
    background: "#1e1b3a",
    transition: "left 0.3s ease",
    padding: sidebarOpen ? "20px" : "0px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    zIndex: 11000,
  }}
>

        <div>
          {/* Profile */}
          <div style={{ textAlign: "center", marginBottom: "30px" }}>
            <FaUserTie size={40} />
            <p style={{ marginTop: "10px", fontWeight: "600" }}>
              {user?.name || "Teacher"}
            </p>
            <p style={{ fontSize: "13px", color: "#bbb" }}>{user?.email}</p>
          </div>

          {/* Navigation */}
          <nav style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <button
              onClick={() => setActiveSection("upload")}
              style={{
                background: activeSection === "upload" ? "#ff4081" : "transparent",
                padding: "10px",
                borderRadius: "10px",
                border: "none",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <FaVideo /> Upload Lecture
            </button>
            <button
              onClick={() => setActiveSection("announcements")}
              style={{
                background:
                  activeSection === "announcements" ? "#ff4081" : "transparent",
                padding: "10px",
                borderRadius: "10px",
                border: "none",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <FaBullhorn /> Announcements
            </button>
            <button
              onClick={() => setActiveSection("students")}
              style={{
                background: activeSection === "students" ? "#ff4081" : "transparent",
                padding: "10px",
                borderRadius: "10px",
                border: "none",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <FaUsers /> Manage Students
            </button>
            <button
              onClick={() => setActiveSection("lectures")}
              style={{
                background: activeSection === "lectures" ? "#ff4081" : "transparent",
                padding: "10px",
                borderRadius: "10px",
                border: "none",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
              }}
            >
              <FaBookOpen /> Uploaded Lectures
            </button>
          </nav>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{
            background: "linear-gradient(90deg,#ff4081,#f50057)",
            padding: "10px",
            borderRadius: "10px",
            border: "none",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <FiLogOut /> Logout
        </button>
      </aside>

    {/* Sidebar Toggle */}
{/* Sidebar Toggle */}
{/* Sidebar Toggle */}
<div
  style={{
    position: "fixed",   // lock to viewport
    top: "12px",
    left: "12px",        // ✅ force left corner
    zIndex: 20000,       // always above everything
  }}
>
  <button
    onClick={() => setSidebarOpen(!sidebarOpen)}
    style={{
      background: "transparent",
      border: "none",
      padding: "10px",
      borderRadius: "8px",
      color: "#fff",
      cursor: "pointer",
      fontSize: "22px",
    }}
  >
    {sidebarOpen ? <FaTimes /> : <FaBars />}
  </button>
</div>



      {/* Main Content */}
  {/* Main Content */}
<main
  style={{
    flex: 1,
    padding: "30px",
    transition: "margin-left 0.3s ease",
    width: "100%",
    marginLeft: window.innerWidth > 768 && sidebarOpen ? "260px" : "0px", // ✅ Correct place
  }}
>
  {/* Course Selector Always Visible */}
  <section
    style={{
      background: "#2a2650",
      padding: "20px",
      borderRadius: "14px",
      marginBottom: "25px",
    }}
  >

          <label
            style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}
          >
            Select Course
          </label>
          <select
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "12px",
              border: "none",
              fontWeight: "600",
              color: "#302b63",
            }}
          >
            <option value="">-- Select Course --</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </section>

        {/* Sections */}
        {activeSection === "upload" && selectedCourseId && (
          <section>
            <h2 style={{ color: "#ff80ab", marginBottom: "12px" }}>
              Upload Lecture
            </h2>
            <input
              type="text"
              placeholder="Title"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                borderRadius: "8px",
              }}
            />
            <input
              type="text"
              placeholder="Video Link (optional)"
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                borderRadius: "8px",
              }}
            />
            {pdfLinks.map((link, idx) => (
              <input
                key={idx}
                type="text"
                placeholder={`PDF Link ${idx + 1}`}
                value={link}
                onChange={(e) => {
                  const updatedLinks = [...pdfLinks];
                  updatedLinks[idx] = e.target.value;
                  setPdfLinks(updatedLinks);
                }}
                style={{
                  width: "100%",
                  marginBottom: "10px",
                  padding: "10px",
                  borderRadius: "8px",
                }}
              />
            ))}
            {uploadError && <p style={{ color: "#f50057", marginBottom: "8px" }}>{uploadError}</p>}
            <button
              onClick={handleLectureUpload}
              style={{
                background: "linear-gradient(90deg,#ff4081,#f50057)",
                padding: "10px 18px",
                borderRadius: "12px",
                border: "none",
                color: "#fff",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Upload
            </button>
          </section>
        )}

        {activeSection === "announcements" && selectedCourseId && (
          <section>
            <h2 style={{ color: "#ff80ab", marginBottom: "12px" }}>
              Announcements
            </h2>
            <textarea
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              placeholder="Write an announcement..."
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                borderRadius: "8px",
              }}
            />
            <button
              onClick={handlePostAnnouncement}
              style={{
                background: "linear-gradient(90deg,#ff4081,#f50057)",
                padding: "10px 18px",
                borderRadius: "12px",
                border: "none",
                color: "#fff",
                fontWeight: "600",
                cursor: "pointer",
                marginBottom: "15px",
              }}
            >
              Post
            </button>
            <ul>
              {announcements.map((a) => (
                <li
                  key={a.id}
                  style={{
                    background: "#1e1b3a",
                    padding: "10px",
                    borderRadius: "10px",
                    marginBottom: "8px",
                  }}
                >
                  {a.content}
                  <button
                    onClick={() => handleDeleteAnnouncement(a.id)}
                    style={{
                      marginLeft: "10px",
                      background: "red",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "6px",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {activeSection === "students" && selectedCourseId && (
          <section>
            <h2
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#ff80ab",
              }}
              onClick={() => setShowStudents((prev) => !prev)}
            >
              Manage Students {showStudents ? <FaChevronUp /> : <FaChevronDown />}
            </h2>
            {showStudents && (
              <>
                <input
                  type="text"
                  placeholder="Search students..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  style={{
                    marginBottom: "10px",
                    padding: "10px",
                    width: "100%",
                    borderRadius: "8px",
                  }}
                />
                <ul>
                  {filteredStudents.map((s) => (
                    <li
                      key={s.id}
                      style={{
                        background: "#1e1b3a",
                        padding: "8px",
                        borderRadius: "8px",
                        marginBottom: "6px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      {s.name} ({s.email})
                      <button
                        onClick={() => handleRemoveStudent(s.id)}
                        style={{
                          background: "red",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          color: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
                <select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  style={{
                    padding: "10px",
                    width: "100%",
                    borderRadius: "8px",
                    marginBottom: "10px",
                  }}
                >
                  <option value="">-- Select Student --</option>
                  {[...students]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.email})
                      </option>
                    ))}
                </select>
                <button
                  onClick={handleEnrollStudent}
                  style={{
                    background: "linear-gradient(90deg,#ff4081,#f50057)",
                    padding: "10px 18px",
                    borderRadius: "12px",
                    border: "none",
                    color: "#fff",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Enroll
                </button>
                {enrollMessage && <p>{enrollMessage}</p>}
              </>
            )}
          </section>
        )}

        {activeSection === "lectures" && selectedCourseId && (
          <section>
            <h2 style={{ color: "#ff80ab", marginBottom: "20px" }}>📚 Lectures</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "20px",
              }}
            >
              {lectures.map((lec) => (
                <div
                  key={lec.id}
                  style={{
                    background: "#1e1b3a",
                    borderRadius: "14px",
                    padding: "18px",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      marginBottom: "12px",
                      color: "#ff80ab",
                    }}
                  >
                    {lec.title}
                  </h3>
                  <button
                    onClick={() => togglePreview(lec.id)}
                    style={{
                      background: "linear-gradient(90deg,#ff4081,#f50057)",
                      color: "#fff",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "10px",
                      fontWeight: "600",
                      cursor: "pointer",
                      marginBottom: "15px",
                    }}
                  >
                    {previewedLectures.includes(lec.id)
                      ? "Hide Details"
                      : "View Details"}
                  </button>

                  {previewedLectures.includes(lec.id) && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "20px",
                        alignItems: "flex-start",
                        marginTop: "10px",
                      }}
                    >
                      {/* Left Inputs */}
                      <div
                        style={{
                          flex: "1",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <input
                          value={editState[lec.id]?.title ?? lec.title}
                          onChange={(e) =>
                            handleEditLecture(lec.id, "title", e.target.value)
                          }
                          placeholder="Lecture Title"
                          style={{
                            padding: "10px",
                            borderRadius: "8px",
                            border: "none",
                          }}
                        />
                        <input
                          value={editState[lec.id]?.video_url ?? lec.video_url}
                          onChange={(e) =>
                            handleEditLecture(lec.id, "video_url", e.target.value)
                          }
                          placeholder="Video URL"
                          style={{
                            padding: "10px",
                            borderRadius: "8px",
                            border: "none",
                          }}
                        />
                        {lec.pdf_urls?.map((url, idx) => (
                          <input
                            key={idx}
                            value={editState[lec.id]?.pdf_urls?.[idx] ?? url ?? ""}
                            onChange={(e) =>
                              handlePdfLinkChange(lec.id, idx, e.target.value)
                            }
                            placeholder={`PDF URL ${idx + 1}`}
                            style={{
                              padding: "10px",
                              borderRadius: "8px",
                              border: "none",
                            }}
                          />
                        ))}
                        <div
                          style={{ display: "flex", gap: "10px", marginTop: "10px" }}
                        >
                          <button
                            onClick={() => handleSaveLecture(lec.id)}
                            style={{
                              background: "green",
                              color: "#fff",
                              border: "none",
                              padding: "8px 14px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => handleDeleteLecture(lec.id)}
                            style={{
                              background: "red",
                              color: "#fff",
                              border: "none",
                              padding: "8px 14px",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Right Preview */}
                      <div style={{ flex: "1" }}>
                        {lec.video_url ? (
                          <iframe
                            src={lec.video_url}
                            width="100%"
                            height="260"
                            allowFullScreen
                            title={`Lecture Preview ${lec.id}`}
                            style={{ borderRadius: "10px", marginBottom: "10px" }}
                          ></iframe>
                        ) : null}
                        {lec.pdf_urls?.length > 0 && (
                          <div>
                            {lec.pdf_urls.map((url, index) => (
                              <a
                                key={index}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "block",
                                  color: "#ff80ab",
                                  marginBottom: "6px",
                                  fontWeight: "500",
                                }}
                              >
                                📄 View PDF {index + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {previewedLectures.includes(lec.id) && (
                    <div style={{ marginTop: "15px" }}>
                      <h4 style={{ marginBottom: "8px", color: "#ff80ab" }}>
                        Access Control
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "10px",
                        }}
                      >
                        {enrolledStudents.map((s) => (
                          <label
                            key={s.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              background: "#2a2650",
                              padding: "6px 10px",
                              borderRadius: "8px",
                              fontSize: "14px",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={lectureAccessMap[lec.id]?.[s.id] ?? true}
                              onChange={(e) =>
                                handleAccessToggle(lec.id, s.id, e.target.checked)
                              }
                            />
                            <span>{s.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default TeacherDashboard;
